import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import {
  format,
  parse,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  isSameDay,
  isSameMonth,
  isWeekend,
} from "date-fns";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";

import { IoPrint } from "react-icons/io5";
import { FaRegFilePdf } from "react-icons/fa";
import { RiFileExcel2Line } from "react-icons/ri";
import { TfiMapAlt } from "react-icons/tfi";

import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";

import "./roomCalendar.css";
import "./Overview.css";
import EAIMLogo from "../img/TMIS_logo.png";
import EAIMLogoPurple from "../img/TMIS_logo.png";

const API = process.env.REACT_APP_API || "";
const ENDPOINT = `${API}/roomSchedules`;
const ROOMS_ENDPOINT = `${API}/getZone`;

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay: (d) => d.getDay(),
  locales,
});

function fmtRange(start, end) {
  return `${format(start, "hh:mm a")} - ${format(end, "hh:mm a")}`;
}
function cleanTitle(t) {
  const s = String(t || "").trim();
  return s || "(No title)";
}
function keyDay(d) {
  return format(d, "yyyy-MM-dd");
}

function getRangeForView(view, date) {
  if (view === "day") {
    const from = new Date(date);
    from.setHours(0, 0, 0, 0);
    const to = new Date(date);
    to.setHours(23, 59, 59, 999);
    return { from, to };
  }
  if (view === "week" || view === "room") {
    const from = startOfWeek(date, { weekStartsOn: 1 });
    const to = endOfWeek(date, { weekStartsOn: 1 });
    return { from, to };
  }
  const from = startOfMonth(date);
  const to = endOfMonth(date);
  return { from, to };
}

function normalizeZone(z) {
  const v = String(z || "")
    .trim()
    .toLowerCase();
  if (v === "green" || v === "red") return v;
  return v || "unknown";
}

function isAfter6Start(ev) {
  if (!ev?.start) return false;
  const start = new Date(ev.start);
  const threshold = new Date(start);
  threshold.setHours(18, 0, 0, 0);
  return start >= threshold;
}

function passesTimeOnlyRule(ev) {
  if (!ev?.start) return false;
  const d = new Date(ev.start);
  if (isWeekend(d)) return true;
  return isAfter6Start(ev);
}

function buildTimeOnlyEvents_MinMax_ByDayZone_WithAircon(
  eventsForTimeOnly,
  roomMetaMap,
) {
  const map = new Map();

  for (const ev of eventsForTimeOnly) {
    const dayDate = new Date(ev.start);
    const day = keyDay(dayDate);

    const room = String(ev?.resource?.room || "").trim();
    const meta = roomMetaMap?.get?.(room) || {};

    const zone = normalizeZone(meta.zone ?? ev?.resource?.zone);
    const aircon = String(meta.aircon ?? ev?.resource?.aircon ?? "").trim();

    const k = `${day}|${zone}`;

    const cur = map.get(k);
    if (!cur) {
      map.set(k, {
        minStart: ev.start,
        maxEnd: ev.end,
        zone,
        weekend: isWeekend(dayDate),
        airconSet: new Set(aircon ? [aircon] : []),
        roomsSet: new Set(room ? [room] : []),
      });
      continue;
    }

    if (ev.start < cur.minStart) cur.minStart = ev.start;
    if (ev.end > cur.maxEnd) cur.maxEnd = ev.end;

    if (aircon) cur.airconSet.add(aircon);
    if (room) cur.roomsSet.add(room);

    map.set(k, cur);
  }

  const out = [];
  for (const [k, v] of map.entries()) {
    const [day, zone] = k.split("|");

    const aircons = Array.from(v.airconSet).filter(Boolean);
    const aircon1 = aircons[0] || "";
    const aircon2 = aircons[1] || "";
    const extraCount = aircons.length > 2 ? aircons.length - 2 : 0;

    out.push({
      id: `timeonly-${day}-${zone}`,
      title: v.weekend
        ? `Time only (Weekend) - ${zone.toUpperCase()} zone`
        : `Time only (Weekday: After 6:00 PM) - ${zone.toUpperCase()} zone`,
      start: v.minStart,
      end: v.maxEnd,
      resource: { zone },
      __timeOnly: true,
      __timeOnlyWeekend: v.weekend,
      __zone: zone,
      __aircon1: aircon1,
      __aircon2: aircon2,
      __airconExtra: extraCount,
      __roomsCount: v.roomsSet.size,
    });
  }

  out.sort((a, b) => a.start - b.start);
  return out;
}

/* =========================
   WEEK LIST GRID
========================= */
function WeekListGrid({ date, events }) {
  const start = startOfWeek(date, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));

  const byDay = useMemo(() => {
    const map = new Map();
    for (const d of days) map.set(keyDay(d), []);

    for (const ev of events) {
      const k = keyDay(ev.start);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(ev);
    }
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => a.start - b.start);
      map.set(k, list);
    }
    return map;
  }, [events, days]);

  return (
    <div className="rc-weekgrid">
      <div className="rc-weekgrid-head">
        {days.map((d) => (
          <div key={d.toISOString()} className="rc-weekgrid-colhead">
            <div className="rc-weekgrid-colhead-inner">
              <div className="rc-weekgrid-date">{format(d, "dd")}</div>
              <div className="rc-weekgrid-dow">{format(d, "EEE")}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="rc-weekgrid-body">
        {days.map((d) => {
          const k = keyDay(d);
          const list = byDay.get(k) || [];
          const isToday = isSameDay(d, new Date());

          return (
            <div
              key={k}
              className={`rc-weekgrid-day ${isToday ? "rc-weekgrid-today" : ""}`}
            >
              {list.length === 0 ? (
                <div className="rc-weekgrid-empty">No events</div>
              ) : (
                list.map((ev) => {
                  const z = normalizeZone(ev?.resource?.zone || ev.__zone);

                  if (ev.__timeOnly) {
                    const a1 = ev.__aircon1 || "";
                    const a2 = ev.__aircon2 || "";
                    const extra = ev.__airconExtra || 0;
                    const airconLine =
                      [a1, a2].filter(Boolean).join(" / ") +
                      (extra ? ` (+${extra})` : "");

                    return (
                      <div
                        key={ev.id}
                        className={[
                          "rc-week-item",
                          "rc-week-item-timeonly",
                          `rc-zone-${z}`,
                        ].join(" ")}
                        title={ev.title}
                        draggable={false}
                        onDragStart={(e) => e.preventDefault()}
                      >
                        <div className="rc-week-item-row">
                          <span className={`rc-zone-badge rc-zone-${z}`}>
                            {z.toUpperCase()}
                          </span>
                          <span className="rc-week-item-time">
                            {fmtRange(ev.start, ev.end)}
                          </span>
                        </div>
                        <div className="rc-week-item-title">
                          {airconLine || "—"}{" "}
                          {ev.__roomsCount ? ` • ${ev.__roomsCount} rooms` : ""}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={ev.id}
                      className="rc-week-item"
                      title={ev.title}
                      draggable={false}
                      onDragStart={(e) => e.preventDefault()}
                    >
                      <span className="rc-week-item-room">
                        {ev?.resource?.room || "-"}
                      </span><br/>
                      <span className="rc-week-item-time">
                        {fmtRange(ev.start, ev.end)}
                      </span>
                      <div className="rc-week-item-title">{ev.title}</div>
                    </div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DayListGrid({ date, events }) {
  const list = useMemo(() => {
    const sameDay = (events || []).filter((ev) => isSameDay(ev.start, date));
    sameDay.sort((a, b) => a.start - b.start);
    return sameDay;
  }, [events, date]);

  const isToday = isSameDay(date, new Date());

  return (
    <div className="rc-daylist">
      <div className={`rc-daylist-head ${isToday ? "rc-daylist-today" : ""}`}>
        <div className="rc-daylist-title">
          {format(date, "EEEE")}
          <span className="rc-daylist-date">{format(date, "dd MMM yyyy")}</span>
        </div>
      </div>

      <div className="rc-daylist-body">
        {list.length === 0 ? (
          <div className="rc-weekgrid-empty">No events</div>
        ) : (
          list.map((ev) => {
            const z = normalizeZone(ev?.resource?.zone || ev.__zone);

            if (ev.__timeOnly) {
              const a1 = ev.__aircon1 || "";
              const a2 = ev.__aircon2 || "";
              const extra = ev.__airconExtra || 0;
              const airconLine =
                [a1, a2].filter(Boolean).join(" / ") +
                (extra ? ` (+${extra})` : "");

              return (
                <div
                  key={ev.id}
                  className={[
                    "rc-week-item",
                    "rc-week-item-timeonly",
                    `rc-zone-${z}`,
                  ].join(" ")}
                  title={ev.title}
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                >
                  <div className="rc-week-item-row">
                    <span className={`rc-zone-badge rc-zone-${z}`}>
                      {z.toUpperCase()}
                    </span>
                    <span className="rc-week-item-time">
                      {fmtRange(ev.start, ev.end)}
                    </span>
                  </div>
                  <div className="rc-week-item-title">
                    {airconLine || "—"}{" "}
                    {ev.__roomsCount ? ` • ${ev.__roomsCount} rooms` : ""}
                  </div>
                </div>
              );
            }

            return (
              <div
                key={ev.id}
                className="rc-week-item"
                title={ev.title}
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              >
                <div className="rc-week-item-row">
                  <span className="rc-week-item-room">
                    {ev?.resource?.room || "-"}
                  </span>
                  <span className="rc-week-item-time">
                    {fmtRange(ev.start, ev.end)}
                  </span>
                </div>
                <div className="rc-week-item-title">{ev.title}</div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const SLOT_DEFS = [
  {
    key: "s1",
    label: "8:45am to 11:45am",
    startMin: 8 * 60 + 45,
    endMin: 11 * 60 + 45,
    cls: "slot-1",
  },
  {
    key: "s2",
    label: "12:00pm to 3:00pm",
    startMin: 12 * 60,
    endMin: 15 * 60,
    cls: "slot-2",
  },
  {
    key: "s3",
    label: "3:15pm to 6:15pm",
    startMin: 15 * 60 + 15,
    endMin: 18 * 60 + 15,
    cls: "slot-3",
  },
  {
    key: "s4",
    label: "7:00pm to 10:00pm",
    startMin: 19 * 60,
    endMin: 22 * 60,
    cls: "slot-4",
  },
];

function minsOfDay(d) {
  const x = new Date(d);
  return x.getHours() * 60 + x.getMinutes();
}
function overlapsSlot(ev, slot) {
  const s = minsOfDay(ev.start);
  const e = minsOfDay(ev.end);
  return s < slot.endMin && e > slot.startMin;
}

function RoomWeekByRoomGrid({ date, events, roomMetaMap }) {
  const weekStart = startOfWeek(date, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(date, { weekStartsOn: 1 });

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const eventsByRoom = useMemo(() => {
    const map = new Map();

    (events || []).forEach((ev) => {
      const r = (ev?.resource?.room || "").trim() || "Unknown";
      const s = new Date(ev.start);
      if (s < weekStart || s > weekEnd) return;

      if (!map.has(r)) map.set(r, []);
      map.get(r).push(ev);
    });

    for (const [r, list] of map.entries()) {
      list.sort((a, b) => a.start - b.start);
      map.set(r, list);
    }
    return map;
  }, [events, weekStart, weekEnd]);

  const rooms = useMemo(
    () => Array.from(eventsByRoom.keys()).sort((a, b) => a.localeCompare(b)),
    [eventsByRoom],
  );

  const renderCellEvents = (list, dayDate, slot) => {
    const sameDay = list.filter((ev) => isSameDay(ev.start, dayDate));
    const inSlot = sameDay.filter((ev) => overlapsSlot(ev, slot));
    if (inSlot.length === 0) return null;

    return (
      <div className="rv-cell-stack">
        {inSlot.map((ev) => (
          <div key={ev.id} className="rv-ev">
            <div className="rv-ev-title">{ev.title}</div>
            <div className="rv-ev-time">{fmtRange(ev.start, ev.end)}</div>
          </div>
        ))}
      </div>
    );
  };

  if (rooms.length === 0)
    return <div className="rc-weekgrid-empty">No events</div>;

  return (
    <div className="rv-wrap">
      {rooms.map((room) => {
        const list = eventsByRoom.get(room) || [];
        if (!list.length) return null;

        const meta = roomMetaMap?.get?.(room) || {};
        const pax = meta?.pax ?? meta?.Pax ?? null;

        return (
          <div key={room} className="rv-room-block">
            <table className="rv-table">
              <thead>
                <tr>
                  <th className="rv-th rv-th-time">Time</th>
                  <th className="rv-th rv-th-room">
                    <div className="rv-room-head">
                      <div className="rv-room-name">{room}</div>
                      <div className="rv-room-pax">
                        {pax != null ? `${pax} Pax` : ""}
                      </div>
                    </div>
                  </th>
                  {days.map((d) => (
                    <th key={d.toISOString()} className="rv-th rv-th-day">
                      {format(d, "EEEE")}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {SLOT_DEFS.map((slot) => (
                  <tr key={slot.key}>
                    <td className={`rv-td rv-td-time ${slot.cls}`}>
                      {slot.label}
                    </td>
                    <td className="rv-td rv-td-room" />
                    {days.map((d) => (
                      <td
                        key={`${room}-${slot.key}-${d.toISOString()}`}
                        className="rv-td rv-td-day"
                      >
                        {renderCellEvents(list, d, slot)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

/* =========================
   LOGO (PRINT + PDF)
========================= */
async function toDataUrl(imgSrc) {
  if (!imgSrc) return null;
  if (String(imgSrc).startsWith("data:")) return imgSrc;

  return await new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth || img.width;
        c.height = img.naturalHeight || img.height;
        const ctx = c.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(c.toDataURL("image/png"));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = imgSrc;
  });
}

async function exportNodeToPdfMultiPage(node, filenameBase, logoDataUrl) {
  document.documentElement.classList.add("rc-printing");
  await new Promise((r) => setTimeout(r, 80));

  const canvas = await html2canvas(node, {
    scale: 3,
    useCORS: true,
    backgroundColor: "#ffffff",
    scrollX: 0,
    scrollY: -window.scrollY,
    windowWidth: document.documentElement.clientWidth,
  });

  document.documentElement.classList.remove("rc-printing");

  const imgData = canvas.toDataURL("image/png");

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  const margin = 4;
  const usableW = pageW - margin * 2;
  const usableH = pageH - margin * 2;

  const imgW = usableW;
  const imgH = (canvas.height * imgW) / canvas.width;

  const logoW = 38;
  const logoH = 18;
  const logoX = pageW - margin - logoW;
  const logoY = pageH - margin - logoH;

  const stampLogo = () => {
    if (!logoDataUrl) return;
    try {
      pdf.addImage(
        logoDataUrl,
        "PNG",
        logoX,
        logoY,
        logoW,
        logoH,
        undefined,
        "FAST",
      );
    } catch {}
  };

  if (imgH <= usableH) {
    pdf.addImage(imgData, "PNG", margin, margin, imgW, imgH, undefined, "FAST");
    stampLogo();
    pdf.save(`${filenameBase}.pdf`);
    return;
  }

  pdf.addImage(imgData, "PNG", margin, margin, imgW, imgH, undefined, "FAST");
  stampLogo();

  let remaining = imgH - usableH;
  let pageIndex = 1;

  while (remaining > 0) {
    pdf.addPage();
    const yOffset = margin - usableH * pageIndex;
    pdf.addImage(
      imgData,
      "PNG",
      margin,
      yOffset,
      imgW,
      imgH,
      undefined,
      "FAST",
    );
    stampLogo();
    remaining -= usableH;
    pageIndex += 1;
  }

  pdf.save(`${filenameBase}.pdf`);
}

/* =========================
   EXCEL EXPORT
========================= */
function buildExcelRows(view, date, events) {
  const rows = (events || []).map((ev) => {
    const z = normalizeZone(ev?.resource?.zone || ev.__zone);

    const room = String(ev?.resource?.room || "").trim();

    const roomsCount = Number(ev.__roomsCount || 0);

    const classroom = ev.__timeOnly
      ? roomsCount > 0
        ? `Multiple (${roomsCount} rooms)`
        : "Multiple"
      : room || "-";

    return {
      Date: format(ev.start, "dd-MM-yyyy"),
      Day: format(ev.start, "EEEE"),
      Start: format(ev.start, "HH:mm"),
      End: format(ev.end, "HH:mm"),
      Zone: z,
      Classroom: classroom,
      Title: ev.__timeOnly ? "Time only" : ev.title,
    };
  });

  rows.sort((a, b) => {
    const ad = a.Date.localeCompare(b.Date);
    if (ad !== 0) return ad;
    return a.Start.localeCompare(b.Start);
  });

  return rows;
}

function exportEventsToExcel(view, date, events, filenameBase) {
  const rows = buildExcelRows(view, date, events);

  const aoa = [];
  aoa.push(["View", view]);
  aoa.push(["Date", "Day", "Start", "End", "Zone", "Classroom", "Title"]);

  rows.forEach((r) => {
    aoa.push([r.Date, r.Day, r.Start, r.End, r.Zone, r.Classroom, r.Title]);
  });

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws["!cols"] = [
    { wch: 12 },
    { wch: 12 },
    { wch: 7 },
    { wch: 7 },
    { wch: 10 },
    { wch: 18 },
    { wch: 55 },
  ];

  ws["!freeze"] = { xSplit: 0, ySplit: 2 };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Schedules");
  XLSX.writeFile(wb, `${filenameBase}.xlsx`);
}

export default function RoomCalendar() {
  const [view, setView] = useState("week");
  const [date, setDate] = useState(new Date());
  const [loading, setLoading] = useState(false);

  const [roomQuery, setRoomQuery] = useState("");
  const [subjectQuery, setSubjectQuery] = useState("");

  const [onlyEvening, setOnlyEvening] = useState(false);
  const [timeOnly, setTimeOnly] = useState(false);

  const [events, setEvents] = useState([]);
  const [note, setNote] = useState("");
  const [windowInfo, setWindowInfo] = useState(null);

  const [showMoreOpen, setShowMoreOpen] = useState(false);
  const [showMoreDate, setShowMoreDate] = useState(null);
  const [showMoreEvents, setShowMoreEvents] = useState([]);

  const [roomZoneMap, setRoomZoneMap] = useState(() => new Map());
  const [roomMetaMap, setRoomMetaMap] = useState(() => new Map());

  const abortRef = useRef(null);
  const roomsAbortRef = useRef(null);
  const printAreaRef = useRef(null);

  const navigate = useNavigate();

  const fetchRoomZones = useCallback(async () => {
    try {
      if (roomsAbortRef.current) roomsAbortRef.current.abort();
      roomsAbortRef.current = new AbortController();

      const res = await fetch(ROOMS_ENDPOINT, {
        signal: roomsAbortRef.current.signal,
        headers: { Accept: "application/json" },
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success) return;

      const zoneMap = new Map();
      const metaMap = new Map();

      (json.data || []).forEach((r) => {
        const room = String(r.room_name || r.room || "").trim();
        if (!room) return;

        const zone = normalizeZone(r.zone);
        const pax = r.Pax ?? r.pax ?? null;

        const aircon =
          r.aircon ?? r.air_con ?? r.aircon_unit ?? r.aircon_name ?? "";

        zoneMap.set(room, zone);
        metaMap.set(room, { zone, pax, aircon });
      });

      setRoomZoneMap(zoneMap);
      setRoomMetaMap(metaMap);
    } catch (e) {
      if (e?.name === "AbortError") return;
    }
  }, []);

  useEffect(() => {
    fetchRoomZones();
    return () => {
      if (roomsAbortRef.current) roomsAbortRef.current.abort();
    };
  }, [fetchRoomZones]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setNote("");

    try {
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();

      const qs = new URLSearchParams();
      if (roomQuery.trim()) qs.set("rooms", roomQuery.trim());
      if (subjectQuery.trim()) qs.set("subject", subjectQuery.trim());

      const { from, to } = getRangeForView(view, date);
      qs.set("from", from.toISOString());
      qs.set("to", to.toISOString());

      const res = await fetch(`${ENDPOINT}?${qs.toString()}`, {
        signal: abortRef.current.signal,
        headers: { Accept: "application/json" },
      });

      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.success)
        throw new Error(json?.message || `Failed (HTTP ${res.status}).`);

      setWindowInfo(json.window || null);

      const mapped = (json.data || [])
        .map((ev, idx) => {
          const start = new Date(ev.start);
          const end = new Date(ev.end);
          if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()))
            return null;

          const room = String(ev.room || "").trim();

          return {
            id: `${ev.email || room || "ev"}-${idx}-${start.getTime()}`,
            title: cleanTitle(ev.title),
            start,
            end,
            resource: {
              room,
              email: ev.email || "",
              location: ev.location || "",
            },
          };
        })
        .filter(Boolean);

      setEvents(mapped);
      if (!mapped.length)
        setNote(json.note || "No events found for this date range.");
    } catch (e) {
      if (e?.name === "AbortError") return;
      setNote(e?.message || "Failed to load schedules.");
      setEvents([]);
      setWindowInfo(null);
    } finally {
      setLoading(false);
    }
  }, [roomQuery, subjectQuery, view, date]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const onPrint = useCallback(() => {
    document.documentElement.classList.add("rc-printing");

    if (view === "day") document.documentElement.classList.add("rc-view-day");
    else document.documentElement.classList.remove("rc-view-day");

    window.print();

    setTimeout(() => {
      document.documentElement.classList.remove("rc-printing");
      document.documentElement.classList.remove("rc-view-day");
    }, 300);
  }, [view]);

  useEffect(() => {
    const onAfter = () =>
      document.documentElement.classList.remove("rc-printing");
    window.addEventListener("afterprint", onAfter);
    return () => window.removeEventListener("afterprint", onAfter);
  }, []);

  const printMonthLabel = useMemo(() => {
    if (view === "day") return format(date, "dd MMM yyyy");
    if (view === "week" || view === "room") {
      return `Week of ${format(startOfWeek(date, { weekStartsOn: 1 }), "dd MMM yyyy")}`;
    }
    return format(date, "MMMM yyyy");
  }, [view, date]);

  const printDateLabel = useMemo(
    () => format(new Date(), "dd/MM/yyyy, hh:mm a"),
    [],
  );

  const eventsWithZone = useMemo(() => {
    const zMap = roomZoneMap;
    const metaMap = roomMetaMap;

    return (events || []).map((ev) => {
      const room = String(ev?.resource?.room || "").trim();
      const zone = normalizeZone(zMap.get(room));
      const meta = metaMap.get(room) || {};

      return {
        ...ev,
        resource: {
          ...ev.resource,
          zone,
          aircon: meta.aircon ?? "",
        },
      };
    });
  }, [events, roomZoneMap, roomMetaMap]);

  const displayEvents = useMemo(() => {
    const base = eventsWithZone || [];

    if (timeOnly) {
      const filtered = base.filter(passesTimeOnlyRule);
      return buildTimeOnlyEvents_MinMax_ByDayZone_WithAircon(
        filtered,
        roomMetaMap,
      );
    }

    if (onlyEvening) return base.filter(isAfter6Start);
    return base;
  }, [eventsWithZone, onlyEvening, timeOnly, roomMetaMap]);

  const onShowMore = useCallback((evts, d) => {
    const list = [...(evts || [])].sort((a, b) => a.start - b.start);
    setShowMoreDate(d);
    setShowMoreEvents(list);
    setShowMoreOpen(true);
  }, []);

  const safeBaseName = useMemo(() => {
    const safe = printMonthLabel.replace(/[^\w\s-]/g, "").replace(/\s+/g, "_");
    return `CALENDAR_${safe}`;
  }, [printMonthLabel]);

  const onDownloadPdf = useCallback(async () => {
    const node = printAreaRef.current;
    if (!node) return;

    document.documentElement.classList.add("rc-exporting-pdf");
    document.documentElement.classList.toggle("rc-view-day", view === "day");

    const logoDataUrl = view === "day" ? null : await toDataUrl(EAIMLogoPurple);

    await exportNodeToPdfMultiPage(node, safeBaseName, logoDataUrl);

    document.documentElement.classList.remove("rc-exporting-pdf");
    document.documentElement.classList.remove("rc-view-day");
  }, [safeBaseName, view]);

  const onDownloadExcel = useCallback(() => {
    exportEventsToExcel(view, date, displayEvents, safeBaseName);
  }, [view, date, displayEvents, safeBaseName]);

  const dayInputValue = useMemo(() => format(date, "yyyy-MM-dd"), [date]);

  const onPickDay = useCallback((e) => {
    const v = e.target.value;
    if (!v) return;
    const [yyyy, mm, dd] = v.split("-").map(Number);
    const next = new Date(yyyy, (mm || 1) - 1, dd || 1, 0, 0, 0, 0);
    setDate(next);
  }, []);

  return (
    <div className="mgmt-wrap rc-page">
      <header className="mgmt-header rc-no-print">
        <div className="brand-fallback">
          <img
            src={EAIMLogo}
            alt="EAIM Logo"
            style={{ height: 60, objectFit: "contain" }}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "center" }}>
          <div className="rc-pill-title">Classes Overview</div>
        </div>
      </header>

      <div className="rc-shell">
        <div className="rc-topbar rc-no-print">
          <div className="rc-left-controls">
            <div className="rc-seg">
              <button
                className={`rc-seg-btn ${view === "day" ? "active" : ""}`}
                onClick={() => setView("day")}
                type="button"
              >
                Day
              </button>
              <button
                className={`rc-seg-btn ${view === "week" ? "active" : ""}`}
                onClick={() => setView("week")}
                type="button"
              >
                Week
              </button>
              <button
                className={`rc-seg-btn ${view === "month" ? "active" : ""}`}
                onClick={() => setView("month")}
                type="button"
              >
                Month
              </button>
              <button
                className={`rc-seg-btn ${view === "room" ? "active" : ""}`}
                onClick={() => setView("room")}
                type="button"
              >
                Rooms
              </button>
            </div>

            <label className="rc-check">
              <input
                type="checkbox"
                checked={onlyEvening}
                onChange={(e) => setOnlyEvening(e.target.checked)}
                disabled={timeOnly}
              />
              <span>Evening only (Start after 6:00 PM)</span>
            </label>

            <label className="rc-check">
              <input
                type="checkbox"
                checked={timeOnly}
                onChange={(e) => {
                  const v = e.target.checked;
                  setTimeOnly(v);
                  if (v) setOnlyEvening(false);
                }}
              />
              <span>Time only (Weekday after 6PM, Weekend min/max) + Zone</span>
            </label>
          </div>

          <div className="rc-actions">
            <button
              className="rc-btn rc-btn-print"
              onClick={onPrint}
              type="button"
            >
              <IoPrint size={20} style={{ marginRight: 8 }} />
              Print
            </button>

            <button
              className="rc-btn rc-btn-print"
              onClick={onDownloadPdf}
              type="button"
              disabled={loading}
              style={{ marginLeft: 2 }}
              title="Download PDF"
            >
              <FaRegFilePdf size={20} />
            </button>

            <button
              className="rc-btn rc-btn-print"
              onClick={onDownloadExcel}
              type="button"
              disabled={loading}
              title="Download Excel"
            >
              <RiFileExcel2Line size={20} />
            </button>
          </div>
        </div>

        <div className="rc-monthline rc-no-print mt-3">
          <div />

          <div className="rc-nav" style={{ gap: 10, alignItems: "center" }}>
            <button
              className="rc-nav-btn"
              type="button"
              onClick={() => {
                const next = new Date(date);
                if (view === "day") next.setDate(next.getDate() - 1);
                else if (view === "week" || view === "room")
                  next.setDate(next.getDate() - 7);
                else next.setMonth(next.getMonth() - 1);
                setDate(next);
              }}
            >
              ‹
            </button>

            {view === "day" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <input
                  type="date"
                  value={dayInputValue}
                  onChange={onPickDay}
                  className="rc-day-picker"
                />
              </div>
            )}

            <button
              className="rc-nav-btn"
              type="button"
              onClick={() => {
                const next = new Date(date);
                if (view === "day") next.setDate(next.getDate() + 1);
                else if (view === "week" || view === "room")
                  next.setDate(next.getDate() + 7);
                else next.setMonth(next.getMonth() + 1);
                setDate(next);
              }}
            >
              ›
            </button>
          </div>
        </div>

        {/* ✅ Printable / PDF area */}
        <div ref={printAreaRef} className="rc-pdf-area">
          <div className="rc-print-meta">
            <div className="rc-print-month text-dark text-uppercase fw-bold">
              {printMonthLabel}
            </div>

            <div className="rc-print-date text-dark" style={{ fontSize: 12 }}>
              Printed: {printDateLabel}
            </div>

            {timeOnly && (
              <div className="rc-print-legend">
                {" "}
                <div className="rc-legend-item">
                  {" "}
                  <span className="rc-legend-pill rc-zone-green" />{" "}
                  <span className="rc-legend-text">Green Zone</span>{" "}
                </div>{" "}
                <div className="rc-legend-item">
                  {" "}
                  <span className="rc-legend-pill rc-zone-red" />{" "}
                  <span className="rc-legend-text">Red Zone</span>{" "}
                </div>{" "}
              </div>
            )}
          </div>

          {/* PRINT ONLY: logo bottom-right */}
          <div className="rc-print-footer-logo" aria-hidden="true">
            <img src={EAIMLogoPurple} alt="EAIM" />
          </div>

          {view === "day" ? (
            <DayListGrid date={date} events={displayEvents} />
          ) : view === "week" ? (
            <WeekListGrid date={date} events={displayEvents} />
          ) : view === "room" ? (
            <RoomWeekByRoomGrid
              date={date}
              events={eventsWithZone}
              roomMetaMap={roomMetaMap}
            />
          ) : (
            <div className="rc-cal-wrap rc-calendar-readonly">
              <Calendar
                localizer={localizer}
                events={displayEvents}
                startAccessor="start"
                endAccessor="end"
                date={date}
                view="month"
                onNavigate={(d) => setDate(d)}
                popup={false}
                selectable={false}
                drilldownView="day"
                onDrillDown={(d) => {
                  setDate(d);
                  setView("day");
                }}
                onSelectEvent={() => {}}
                onSelectSlot={() => {}}
                onShowMore={onShowMore}
                showAllEvents={false}
                messages={{ showMore: (total) => `+${total} more` }}
                style={{ height: 720 }}
                components={{
                  event: ({ event }) => {
                    if (event?.__timeOnly) {
                      const z = normalizeZone(
                        event.__zone || event?.resource?.zone,
                      );

                      const a1 = event.__aircon1 || "";
                      const a2 = event.__aircon2 || "";
                      const extra = event.__airconExtra || 0;

                      const airconLine =
                        [a1, a2].filter(Boolean).join(" / ") +
                        (extra ? ` (+${extra})` : "");

                      const title = `${z.toUpperCase()} zone • ${event.__roomsCount || 0} rooms • ${airconLine}`;

                      return (
                        <div
                          className={`rc-event-inner rc-event-timeonly rc-zone-${z}`}
                          title={title}
                        >
                          <div className="rc-event-top">
                            <span
                              className="rc-event-time fw-bold rc-center"
                              style={{ fontSize: 12 }}
                            >
                              {fmtRange(event.start, event.end)}
                              <br />
                              <span
                                style={{
                                  fontSize: 10,
                                  fontWeight: 800,
                                  opacity: 0.95,
                                }}
                              >
                                {airconLine || "—"}
                              </span>
                            </span>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div className="rc-event-inner" title={event.title}>
                        <div className="rc-event-top">
                          <span className="rc-event-room">
                            {event?.resource?.room || "-"}
                          </span>
                          <span className="rc-event-time">
                            {fmtRange(event.start, event.end)}
                          </span>
                        </div>
                        <div className="rc-event-title">{event.title}</div>
                      </div>
                    );
                  },
                }}
                dayPropGetter={(d) => {
                  if (!isSameMonth(d, date))
                    return { className: "rc-day-outside" };
                  if (isSameDay(d, new Date()))
                    return { className: "rc-day-today" };
                  return {};
                }}
              />
            </div>
          )}
        </div>

        {loading ? <div className="rc-empty">Loading…</div> : null}
        {note ? <div className="rc-empty">{note}</div> : null}

        {showMoreOpen && (
          <div
            className="rc-more-backdrop"
            onClick={() => setShowMoreOpen(false)}
          >
            <div className="rc-more-modal" onClick={(e) => e.stopPropagation()}>
              <div className="rc-more-head">
                <div className="rc-more-title">
                  Classes on{" "}
                  {showMoreDate
                    ? format(showMoreDate, "dd MMM yyyy (EEE)")
                    : ""}
                </div>
                <button
                  className="rc-more-close"
                  type="button"
                  onClick={() => setShowMoreOpen(false)}
                >
                  ✕
                </button>
              </div>

              <div className="rc-more-body">
                {showMoreEvents.length === 0 ? (
                  <div className="rc-weekgrid-empty">No events</div>
                ) : (
                  showMoreEvents.map((ev) => {
                    const z = normalizeZone(ev?.resource?.zone || ev.__zone);

                    if (ev.__timeOnly) {
                      const a1 = ev.__aircon1 || "";
                      const a2 = ev.__aircon2 || "";
                      const extra = ev.__airconExtra || 0;
                      const airconLine =
                        [a1, a2].filter(Boolean).join(" / ") +
                        (extra ? ` (+${extra})` : "");

                      return (
                        <div
                          key={ev.id}
                          className={[
                            "rc-week-item",
                            "rc-week-item-timeonly",
                            `rc-zone-${z}`,
                          ].join(" ")}
                        >
                          <div className="rc-week-item-row">
                            <span className={`rc-zone-badge rc-zone-${z}`}>
                              {z.toUpperCase()}
                            </span>
                            <span className="rc-week-item-time">
                              {fmtRange(ev.start, ev.end)}
                            </span>
                          </div>
                          <div className="rc-week-item-title">
                            {airconLine || "—"}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={ev.id} className="rc-week-item">
                        <div className="rc-week-item-row">
                          <span className="rc-week-item-room">
                            {ev?.resource?.room || "-"}
                          </span>
                          <span className="rc-week-item-time">
                            {fmtRange(ev.start, ev.end)}
                          </span>
                        </div>
                        <div className="rc-week-item-title">{ev.title}</div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        <button
          type="button"
          className="rc-fab rc-no-print"
          onClick={() => navigate("/activeroom")}
          title="Go to Campus Digital Map"
        >
          <TfiMapAlt size={25} />
        </button>
      </div>
    </div>
  );
}
